// The player's own Counter-Strike: Source install, used for textures, skies and models that maps
// and the knife need but that we can't ship. Read locally; nothing is uploaded.
// Chromium browsers can keep a handle to the folder (one permission prompt per session);
// elsewhere the player picks the folder each session (only the needed files are read).

import { groupVpkFiles } from '../maps/vpk';
import type { GameVpk } from '../maps/bsp/workerProtocol';
import { clearGameFolder, loadGameFolder, saveGameFolder } from './mapCache';

type DirHandle = FileSystemDirectoryHandle & {
  queryPermission?(o: { mode: 'read' }): Promise<PermissionState>;
  requestPermission?(o: { mode: 'read' }): Promise<PermissionState>;
  values(): AsyncIterable<FileSystemHandle>;
};
type Picker = (o?: { id?: string; mode?: 'read' }) => Promise<DirHandle>;

const USE_DIRECTORY_PICKER = false;

const picker = (): Picker | undefined =>
  (window as unknown as { showDirectoryPicker?: Picker }).showDirectoryPicker;

export class GameFolder {
  /** Folder name for display, null when none chosen. */
  name: string | null = null;
  private handle: DirHandle | null = null;
  private vpks: GameVpk[] | null = null;

  get canRemember() {
    return USE_DIRECTORY_PICKER && !!picker();
  }

  /** Restore the remembered folder (no permission prompt yet). */
  async restore() {
    try {
      this.handle = await loadGameFolder<DirHandle>();
      this.name = this.handle?.name ?? null;
    } catch {
      this.handle = null;
    }
  }

  /**
   * Let the player choose their CS:S folder. Must run inside a click handler.
   * Returns a message describing the result.
   */
  async choose(fallbackInput: HTMLInputElement): Promise<string> {
    // Chromium's folder API refuses everything under C:\Program Files, which is where Steam puts
    // games, so the ordinary folder dialog (an <input webkitdirectory>) is used everywhere instead.
    const pick = USE_DIRECTORY_PICKER ? picker() : undefined;
    if (pick) {
      let h: DirHandle;
      try {
        h = await pick({ id: 'css-folder', mode: 'read' });
      } catch {
        return '';
      }
      const files = await filesFromHandle(h);
      const vpks = toVpks(files);
      if (!vpks.length) return `No game files found in "${h.name}". Choose the Counter-Strike Source folder.`;
      this.handle = h;
      this.vpks = vpks;
      this.name = h.name;
      await saveGameFolder(h).catch(() => {});
      return '';
    }
    // Folder dialog; the choice lasts for this session (fetched textures stay cached with each map).
    fallbackInput.value = '';
    const files = await new Promise<File[]>((resolve) => {
      fallbackInput.onchange = () => resolve([...(fallbackInput.files ?? [])]);
      fallbackInput.click();
    });
    if (!files.length) return '';
    const vpks = toVpks(files.filter((f) => /\.vpk$/i.test(f.name)).map((f) => ({ name: f.name, file: f })));
    if (!vpks.length) return 'No game files found there. Choose the Counter-Strike Source folder.';
    this.vpks = vpks;
    const root = (files[0] as File & { webkitRelativePath?: string }).webkitRelativePath?.split('/')[0];
    this.name = root || 'chosen folder';
    return '';
  }

  /**
   * The game's VPK files, or null. With `interactive` (inside a click) a remembered folder may ask
   * the browser for permission again.
   */
  async files(interactive: boolean): Promise<GameVpk[] | null> {
    if (this.vpks) return this.vpks;
    const h = this.handle;
    if (!h) return null;
    try {
      let state = (await h.queryPermission?.({ mode: 'read' })) ?? 'granted';
      if (state === 'prompt' && interactive)
        state = (await h.requestPermission?.({ mode: 'read' })) ?? 'denied';
      if (state !== 'granted') return null;
      this.vpks = toVpks(await filesFromHandle(h));
      return this.vpks.length ? this.vpks : null;
    } catch {
      return null;
    }
  }

  get ready() {
    return !!this.vpks;
  }

  async forget() {
    this.handle = null;
    this.vpks = null;
    this.name = null;
    await clearGameFolder().catch(() => {});
  }
}

async function filesFromHandle(root: DirHandle): Promise<{ name: string; file: File }[]> {
  const dirs: DirHandle[] = [root];
  for (const sub of ['cstrike', 'hl2']) {
    try {
      dirs.push((await root.getDirectoryHandle(sub)) as DirHandle);
    } catch {
      // Not there (maybe the player picked cstrike itself).
    }
  }
  const out: { name: string; file: File }[] = [];
  for (const d of dirs)
    for await (const h of d.values())
      if (h.kind === 'file' && /\.vpk$/i.test(h.name))
        out.push({ name: h.name, file: await (h as FileSystemFileHandle).getFile() });
  return out;
}

function toVpks(files: { name: string; file: Blob }[]): GameVpk[] {
  return groupVpkFiles(files).map((s) => ({ name: s.name, dir: s.dir, archives: [...s.archives] }));
}
